import { RenderIf } from 'react-rainbow-components';
import CallInformation from 'components/CallInformation';
import CalendarEventInformation from 'components/CalendarEventInformation';
import CustomerNote from 'components/CustomerNote';
import SMSInformation from 'components/SMSInformation';
import priceFormatter from 'data/services/price/formatter';
import ReminderSMSInformation from 'components/ReminderSMSInformation';
import EmailContent from 'components/EmailContent';
import { PaidAdChip } from 'components/PaidAd';
import readMessageAdReferral from 'components/PaidAd/readMessageAdReferral';
import { MetaAdReferral, MultimodalPart } from 'data/firestore/globals';
import getMediaComponent from 'pages/Messages/ThreadMessages/MessageBubble/Media/helpers/getMediaComponent';
import removeActionsTokens from '../../helpers/removeActionsTokens';
import { ChatMessage, DisplayContext } from './types';
import {
    EventContainer, ChatBubble, FromAd, StyledLink,
} from './styled';

const FromAdMarker = ({ referral }: { referral: MetaAdReferral }) => (
    <FromAd>
        <PaidAdChip referral={referral} variant="message" />
    </FromAd>
);

// Type guard to check if content is an array of MultimodalPart
const isMultimodalContent = (
    content: string | Array<MultimodalPart> | undefined,
): content is Array<MultimodalPart> => Array.isArray(content);

// Get URL from binary part (handles url, data, or id)
const getBinaryPartUrl = (part: MultimodalPart): string | undefined => {
    if (part.type !== 'binary') return undefined;

    // Priority: url > data > id
    if (part.url) return part.url;
    if (part.data) return `data:${part.mimeType};base64,${part.data}`;
    if (part.id) return part.id; // Assuming id can be used as URL

    return undefined;
};

const MessageContent = ({
    message,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    context,
    renderContentOnly,
    renderMediaOnly,
    renderMultimodal,
    totalBubbles,
    contentBubbleIndex,
    isCurrentUser,
    role,
}: {
    message: ChatMessage;
    context?: DisplayContext;
    renderContentOnly?: boolean;
    renderMediaOnly?: boolean;
    renderMultimodal?: boolean;
    totalBubbles?: number;
    contentBubbleIndex?: number;
    isCurrentUser?: boolean;
    role?: string;
}) => {
    const { type, content, agentId = '' } = message;
    if (type === 'note') {
        return <CustomerNote {...content} />;
    }

    if (type === 'action') {
        if (content.action === 'inbox-created') return <div>I created this Inbox!</div>;
        if (content.action === 'inbox-read') return <div>I saw this Inbox!</div>;
        if (content.action === 'inbox-solved') return <div>I solved this Inbox ✅!</div>;
        if (content.action === 'inbox-removed') return <div>I removed this Inbox!</div>;
        if (content.action === 'inbox-reopen') return <div>I reopened this Inbox!</div>;
        if (content.action === 'inbox-spam') return <div>I mark this Inbox as spam!</div>;
        if (content.action === 'inbox-not-spam') return <div>I removed this Inbox from spam!</div>;
    }

    if (type === 'call') {
        return (
            <CallInformation
                sessionId={content.sessionId as string}
            />
        );
    }

    if (type === 'calendar-event-created') {
        return (
            <EventContainer>
                <div>
                    I created this new Event!
                </div>
                <CalendarEventInformation
                    agentId={content.agentId as string}
                    eventId={content.eventId as string}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    data={content}
                />
            </EventContainer>
        );
    }

    if (type === 'calendar-event-accepted') {
        return (
            <EventContainer>
                <div>
                    I accepted this Event!
                </div>
                <CalendarEventInformation
                    agentId={content.agentId as string}
                    eventId={content.eventId as string}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    data={content}
                />
            </EventContainer>
        );
    }

    if (type === 'calendar-event-rejected') {
        return (
            <EventContainer>
                <div>
                    I rejected this Event!
                </div>
                <CalendarEventInformation
                    agentId={content.agentId as string}
                    eventId={content.eventId as string}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    data={content}
                />
            </EventContainer>
        );
    }

    if (type === 'calendar-event-removed') {
        return (
            <EventContainer>
                <div>
                    I removed this Event!
                </div>
                <CalendarEventInformation
                    agentId={content.agentId as string}
                    eventId={content.eventId as string}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    data={content}
                />
            </EventContainer>
        );
    }

    if (type === 'calendar-event-rescheduled') {
        return (
            <EventContainer>
                <div>
                    I rescheduled this Event!
                </div>
                <CalendarEventInformation
                    agentId={content.agentId as string}
                    eventId={content.eventId as string}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    data={content}
                />
            </EventContainer>
        );
    }

    if (type === 'calendar-event-sms-sent') {
        return (
            <ReminderSMSInformation smsId={content.smsId} />
        );
    }

    if (type === 'opportunity-created') {
        return (
            <div>
                I created this Opportunity
                {' '}
                with a Score of
                {' '}
                <strong>
                    {content.score as number || ' (-)'}
                </strong>
                {' in stage '}
                <strong>
                    {((content.stage as any)?.name) as string}
                </strong>
                {' of pipeline '}
                <strong>
                    {((content.pipeline as any)?.name) as string}
                </strong>
                !
            </div>
        );
    }

    if (type === 'opportunity-stage-changed') {
        return (
            <div>
                I changed this Opportunity to stage
                {' '}
                <strong>
                    {((content.stage as any)?.name) as string}
                </strong>
                {' in pipeline '}
                <strong>
                    {((content.pipeline as any)?.name) as string}
                </strong>
                !
            </div>
        );
    }

    if (type === 'opportunity-assigned') {
        return (
            <div>
                I assigned
                {' '}
                <strong>
                    {((content.owner as any)?.displayName) as string}
                </strong>
                {' '}
                as the Owner of this Opportunity!
            </div>
        );
    }

    if (type === 'opportunity-unassigned') {
        return (
            <>
                I removed the Owner of this Opportunity!
            </>
        );
    }

    if (type === 'opportunity-removed') {
        return (
            <>
                I removed this Opportunity🗑️!
            </>
        );
    }

    if (type === 'opportunity-qualified') {
        if (content.prevScore === null) {
            return (
                <div>
                    I increased the Opportunity Score to
                    {' '}
                    <strong>
                        {content.newScore as number}
                    </strong>
                    ! 🔥
                </div>
            );
        }

        return (
            <div>
                I changed the Opportunity Score from
                {' '}
                <strong>
                    {content.prevScore as number}
                </strong>
                {' '}
                to
                {' '}
                <strong>
                    {content.newScore as number}
                </strong>
                !
            </div>
        );
    }

    if (type === 'opportunity-unqualified') {
        return (
            <div>
                I removed the Score from this Opportunity🚫!
            </div>
        );
    }

    if (type === 'opportunity-amount-updated') {
        const prevAmount = priceFormatter('usd').format(((content.prevAmount || 0) as number) / 100);
        const newAmount = priceFormatter('usd').format(((content.newAmount || 0) as number) / 100);

        return (
            <div>
                I changed the Opportunity Ammount from
                {' '}
                <strong>
                    {prevAmount}
                </strong>
                {' '}
                to
                {' '}
                <strong>
                    {newAmount}
                </strong>
                !
            </div>
        );
    }

    if (type === 'message-sent') {
        const body = content.text as string;
        const adReferral = readMessageAdReferral(content);
        const media = content.media as any;
        const validMedia = Array.isArray(media)
            ? media.filter((m) => m !== undefined && m !== null)
            : [];

        if (content.channel === 'sms') {
            return (
                <SMSInformation
                    body={body}
                    media={media}
                />
            );
        }

        // New behavior: render multimodal content parts
        if (renderMultimodal && isMultimodalContent(content.content)) {
            const parts = content.content as Array<MultimodalPart>;
            const totalParts = parts.length;
            const firstTextIndex = parts.findIndex(
                (part) => part.type === 'text' && Boolean(removeActionsTokens(part.text || '')),
            );

            return (
                <>
                    {parts.map((part, index) => {
                        const isFirst = index === 0;
                        const isLast = index === totalParts - 1;
                        const isIntermediate = !isFirst && !isLast;

                        // Render text part
                        if (part.type === 'text') {
                            const textContent = part.text || '';
                            const strippedText = removeActionsTokens(textContent);

                            if (!strippedText) return null;

                            return (
                                <ChatBubble
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`text-${index}`}
                                    isCurrentUser={isCurrentUser || false}
                                    role={role}
                                    isFirst={isFirst}
                                    isLast={isLast}
                                    isIntermediate={isIntermediate}
                                >
                                    {adReferral && index === firstTextIndex ? (
                                        <FromAdMarker referral={adReferral} />
                                    ) : null}
                                    <div>{strippedText}</div>
                                </ChatBubble>
                            );
                        }

                        // Render binary part
                        if (part.type === 'binary') {
                            const url = getBinaryPartUrl(part);
                            if (!url) return null;

                            const Component = getMediaComponent(part.mimeType);

                            return (
                                <ChatBubble
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`binary-${index}`}
                                    isCurrentUser={isCurrentUser || false}
                                    role={role}
                                    isFirst={isFirst}
                                    isLast={isLast}
                                    isIntermediate={isIntermediate}
                                >
                                    <Component
                                        url={url}
                                        filename={part.filename || part.mimeType}
                                    />
                                </ChatBubble>
                            );
                        }

                        return null;
                    })}
                </>
            );
        }

        // Legacy behavior: render text and media separately
        if (renderContentOnly) {
            return (
                <>
                    <RenderIf isTrue={!body && validMedia.length === 0}>
                        <div>No message content</div>
                    </RenderIf>
                    {adReferral ? <FromAdMarker referral={adReferral} /> : null}
                    {body && <div>{body}</div>}
                </>
            );
        }

        if (renderMediaOnly && totalBubbles !== undefined && contentBubbleIndex !== undefined) {
            return (
                <>
                    {validMedia.map((mediaItem, index) => {
                        const Component = getMediaComponent(mediaItem?.contentType);
                        const bubbleIndex = contentBubbleIndex >= 0
                            ? contentBubbleIndex + 1 + index
                            : index;
                        const isFirstBubble = contentBubbleIndex < 0 && index === 0;
                        const isLastBubble = bubbleIndex === totalBubbles - 1;
                        const isIntermediateBubble = !isFirstBubble && !isLastBubble;

                        return (
                            <ChatBubble
                                key={mediaItem?.url || index}
                                isCurrentUser={isCurrentUser || false}
                                role={role}
                                isFirst={isFirstBubble}
                                isLast={isLastBubble}
                                isIntermediate={isIntermediateBubble}
                            >
                                <Component
                                    url={mediaItem?.url}
                                    filename={mediaItem?.contentType}
                                />
                            </ChatBubble>
                        );
                    })}
                </>
            );
        }

        return null;
    }

    if (type === 'email-sent') {
        return (
            <EmailContent
                subject={content.subject}
                body={content.body}
                from={content.from}
                to={content.to}
            />
        );
    }

    if (type === 'customer-create') return 'I created this Customer!';
    if (type === 'customer-update') return 'I updated this Customer Information!';
    // TODO: what is the type of `file`?
    // if (type === 'file') return 'I uploaded a File on this Customer!';

    if (type === 'thread-ai-response-status-changed') {
        const threadId = content.threadId as string | undefined;
        const threadSuffix = threadId?.slice(-5);
        const threadTo = agentId && threadId ? `/${agentId}/threads/${threadId}` : null;
        let threadRef: JSX.Element | string;
        if (threadTo && threadSuffix) {
            threadRef = (
                <>
                    thread
                    {' '}
                    <StyledLink to={threadTo}>
                        #
                        {threadSuffix}
                    </StyledLink>
                </>
            );
        } else if (threadSuffix) {
            threadRef = (
                <>
                    thread #
                    {threadSuffix}
                </>
            );
        } else {
            threadRef = 'this thread';
        }
        if (content.aiResponseStatus === 'enabled') {
            return (
                <div>
                    I
                    {' '}
                    enabled
                    {' '}
                    AI responses for
                    {' '}
                    {threadRef}
                    .
                </div>
            );
        }
        if (content.aiResponseStatus === 'disabled') {
            return (
                <div>
                    I
                    {' '}
                    disabled
                    {' '}
                    AI responses for
                    {' '}
                    {threadRef}
                    .
                </div>
            );
        }
        return (
            <div>
                I updated AI response settings for
                {' '}
                {threadRef}
                .
            </div>
        );
    }

    return null;
};

export default MessageContent;
